import { useMemo, useState } from 'react';
import type { ExportFormat, RecordSettings, Recording } from '@glyph/protocol-ts';
import { fmtBytes, fmtClock, useDevice, useRecordings, useSaveSettings } from '@glyph/core';
import {
  Button,
  Card,
  CardTitle,
  CellMono,
  CellText,
  CellTitle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  NavItem,
  NavSection,
  Note,
  PageHeader,
  Section,
  SegmentedControl,
  Select,
  StatusPill,
  TextInput,
  useToast,
} from '@glyph/ui';
import { fmtDateTs, upper } from './shared';

export interface RecordingsScreenProps {
  deviceId?: string;
  /** Cloud: show recordings for all devices; local/device page: this device only. */
  scope?: 'device' | 'fleet';
}

const FMT_NOTES: Record<string, string> = {
  srt: 'Imports natively into Premiere Pro, DaVinci Resolve and Final Cut Pro. Safest choice.',
  vtt: 'Web players and YouTube. Same cues as SRT with styling hints.',
  fcpxml: 'Final Cut Pro caption roles with the project frame rate. Opens straight onto the timeline.',
  scc: 'CEA-608 for broadcast delivery and Premiere closed-caption tracks. 32 characters per line, 2 lines, 29.97 DF only.',
  csv: 'Word-level timecodes for DaVinci markers or your own tooling.',
};
const TC_NOTES: Record<RecordSettings['tc_source'], string> = {
  system: 'Clock disciplined by NTP over the uplink · ±2 ms typical · matches cameras set to time of day.',
  freerun: 'Starts at the offset when the session opens · no external reference.',
  ltc: 'Decodes LTC from channel 2 of the USB interface · jam-syncs to house timecode · needs a cabled feed.',
  atem: 'Polled from the ATEM switcher ~1 Hz · ±1–3 frames · disciplined free run.',
  hyperdeck: 'HyperDeck TCP 9993 poll · ±1–3 frames · disciplined free run.',
  off: 'No timecode: exports count from the session start.',
};
const FPS: RecordSettings['tc_framerate'][] = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];
const fpsLabel = (f: number) => (f === 29.97 ? '29.97 DF' : String(f));
const dur = (r: Recording) => (r.end_ts && r.start_ts ? r.end_ts - r.start_ts : 0);

/** Recordings: folders by event, table, rename/delete, export (SRT/VTT/FCPXML/SCC/CSV), Timecode card. */
export function RecordingsScreen({ deviceId, scope = 'device' }: RecordingsScreenProps) {
  const dev = useDevice(deviceId);
  const recs = useRecordings(scope === 'fleet' ? undefined : deviceId);
  const s = useSaveSettings(deviceId);
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('all');
  const [sel, setSel] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [fmt, setFmt] = useState<ExportFormat>('srt');
  const [exported, setExported] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const all = useMemo(() => recs.data ?? [], [recs.data]);
  const folders = useMemo(() => {
    const m = new Map<string, { name: string; n: number }>();
    for (const r of all)
      m.set(r.folder, { name: r.event_name ?? r.folder, n: (m.get(r.folder)?.n ?? 0) + 1 });
    return [
      ['all', 'All recordings', all.length] as const,
      ...[...m.entries()].map(([k, v]) => [k, v.name, v.n] as const),
    ];
  }, [all]);
  const list = all.filter(
    (r) =>
      (folder === 'all' || r.folder === folder) &&
      `${r.display_name ?? r.name} ${r.event_name ?? ''}`.toLowerCase().includes(search.toLowerCase()),
  );
  const selRec: Recording | undefined = list.find((r) => r.id === sel) ?? list[0];
  const synced = all.filter((r) => r.upload_status === 'synced').length;
  const rec = s.draft?.record;
  const label = (r: Recording) => r.display_name ?? r.name;

  const startRename = () => {
    if (!selRec) return;
    setRenaming(selRec.id);
    setRenameVal(label(selRec));
  };
  const commitRename = async () => {
    const r = all.find((x) => x.id === renaming);
    if (r && renameVal.trim() && renameVal.trim() !== label(r))
      await recs.rename({ name: r.name, display_name: renameVal.trim() });
    setRenaming(null);
  };
  const doExport = async () => {
    if (!selRec) return;
    const res = await recs.export({
      name: selRec.name,
      format: fmt,
      zero: rec?.export_zero ?? null,
      zero_tc: rec?.export_zero_tc ?? null,
      chars_per_line: rec?.export_chars_per_line ?? null,
    });
    setExported(
      `${label(selRec)}.${fmt} · ${(selRec.words ?? 0).toLocaleString()} words · ${fpsLabel(selRec.framerate)} · start ${selRec.tc_start} · ${fmtBytes(res.size)} · ready`,
    );
    if (res.download_url) window.open(res.download_url, '_blank');
    else if (res.content && typeof URL !== 'undefined' && typeof document !== 'undefined') {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.content], { type: 'text/plain' }));
      a.download = `${label(selRec)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  };
  const saveTc = async () => {
    await s.save(['record']);
    toast.push('Timecode settings saved', 'ok');
  };

  const renameCell = (r: Recording) =>
    renaming === r.id ? (
      <TextInput
        variant="inline-rename"
        block
        autoFocus
        value={renameVal}
        onChange={(e) => setRenameVal(e.target.value)}
        onBlur={() => void commitRename()}
        onKeyDown={(e) =>
          e.key === 'Enter' ? e.currentTarget.blur() : e.key === 'Escape' && setRenaming(null)
        }
        onClick={(e) => e.stopPropagation()}
      />
    ) : (
      <span className="truncate text-md font-medium leading-none">{label(r)}</span>
    );
  const syncPill = (r: Recording) => (
    <StatusPill
      tone={r.upload_status === 'synced' ? 'ok' : r.upload_status === 'uploading' ? 'accent' : 'warn'}
    >
      {r.upload_status === 'synced'
        ? 'SYNCED'
        : r.upload_status === 'uploading'
          ? 'UPLOADING'
          : r.upload_status === 'deleted_on_device'
            ? 'CLOUD ONLY'
            : 'LOCAL ONLY'}
    </StatusPill>
  );

  const exportCard = (
    <Card mode="padded" gap="md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-[5px]">
          <span className="truncate text-md font-medium leading-none">{selRec ? label(selRec) : '—'}</span>
          <span className="font-mono text-xs text-fg4">
            {selRec
              ? `${fmtClock(dur(selRec))} · ${(selRec.words ?? 0).toLocaleString()} words · ${fmtBytes(selRec.size)}`
              : 'no selection'}
          </span>
        </div>
        <div className="flex flex-none gap-1.5">
          <Button size="sm" onClick={startRename} disabled={!selRec}>
            Rename
          </Button>
          <Button
            size="sm"
            className="text-bad"
            onClick={() => setConfirmDelete(true)}
            disabled={!selRec || selRec.protected}
          >
            Delete
          </Button>
        </div>
      </div>
      <Field label="Export format">
        <SegmentedControl
          wrap
          value={fmt}
          onChange={setFmt}
          options={(['srt', 'vtt', 'fcpxml', 'scc', 'csv'] as ExportFormat[]).map((f) => ({
            value: f,
            label: f.toUpperCase(),
            disabled: f === 'scc' && !!selRec && selRec.framerate !== 29.97,
          }))}
        />
        <Note>{FMT_NOTES[fmt]}</Note>
      </Field>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="responsive"
          onClick={() => void doExport()}
          disabled={!selRec || recs.isExporting}
        >
          Export {fmt.toUpperCase()}
        </Button>
        <Button
          size="responsive"
          disabled={!selRec}
          onClick={() =>
            selRec &&
            void recs
              .export({ name: selRec.name, format: 'txt' })
              .then((r) => r.download_url && window.open(r.download_url, '_blank'))
          }
        >
          Open transcript
        </Button>
      </div>
      {exported && <Note tone="ok">{exported}</Note>}
    </Card>
  );
  const tcCard = rec && (
    <Card mode="padded" gap="md">
      <CardTitle meta={s.isDirty('record') ? 'UNSAVED' : undefined}>Timecode</CardTitle>
      <Field label="Source">
        <SegmentedControl
          wrap
          value={rec.tc_source}
          onChange={(v) => s.update('record', { tc_source: v })}
          options={[
            { value: 'system', label: 'Time of day' },
            { value: 'freerun', label: 'Free run' },
            { value: 'ltc', label: 'LTC · audio ch 2' },
            { value: 'hyperdeck', label: 'HyperDeck' },
            { value: 'atem', label: 'ATEM' },
          ]}
        />
        <Note>{TC_NOTES[rec.tc_source]}</Note>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Frame rate">
          <Select
            block
            value={String(rec.tc_framerate)}
            onChange={(v) =>
              s.update('record', { tc_framerate: Number(v) as RecordSettings['tc_framerate'] })
            }
            options={FPS.map((f) => ({ value: String(f), label: fpsLabel(f) }))}
          />
        </Field>
        <Field label="Start offset">
          <TextInput
            variant="mono"
            block
            value={rec.tc_start}
            onChange={(e) => s.update('record', { tc_start: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Export zero">
        <SegmentedControl
          value={rec.export_zero}
          onChange={(v) => s.update('record', { export_zero: v })}
          options={[
            { value: 'session', label: 'Session start' },
            { value: 'tc_hour', label: 'TC hour' },
            { value: 'custom', label: 'Custom' },
          ]}
        />
      </Field>
      {s.isDirty('record') && (
        <div className="flex gap-2">
          <Button variant="primary" size="responsive" onClick={() => void saveTc()} disabled={s.isSaving}>
            Save timecode
          </Button>
          <Button size="responsive" onClick={() => s.revert('record')}>
            Revert
          </Button>
        </div>
      )}
    </Card>
  );

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Recordings"
        meta={`${all.length} SESSIONS · ${synced} SYNCED TO CLOUD · ${all.length - synced} LOCAL ONLY · ${upper(dev.data?.id)}`}
        actions={
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, event, words"
            className="w-[260px]"
          />
        }
      />

      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        <TextInput
          block
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, event, words"
        />
        <Select
          block
          value={folder}
          onChange={setFolder}
          options={folders.map(([k, n, c]) => ({ value: k, label: `${n} · ${c}` }))}
          aria-label="Folder"
        />
        <Card>
          {list.length === 0 && (
            <EmptyState label="No recordings" hint="Sessions appear here as soon as the recorder starts." />
          )}
          {list.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setSel(r.id)}
              onKeyDown={(e) => e.key === 'Enter' && setSel(r.id)}
              className={`flex cursor-pointer flex-col gap-[7px] border-t border-line px-4 py-3 ${selRec?.id === r.id ? 'bg-selected' : ''}`}
            >
              <div className="flex items-center justify-between gap-3">
                {renaming === r.id ? (
                  renameCell(r)
                ) : (
                  <span className="min-w-0 truncate text-xl font-medium">{label(r)}</span>
                )}
                {syncPill(r)}
              </div>
              <span className="text-md text-fg2">{r.event_name ?? '—'}</span>
              <span className="font-mono text-sm leading-[1.4] text-fg4">
                {fmtDateTs(r.start_ts)} · {fmtClock(dur(r))} · {(r.words ?? 0).toLocaleString()} words · TC{' '}
                {r.tc_start}
              </span>
            </div>
          ))}
        </Card>
        {exportCard}
        {tcCard}
      </div>

      {/* desktop */}
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <div className="flex min-w-[200px] flex-col gap-[2px]" style={{ flex: '0 1 220px' }}>
          <NavSection>Folders</NavSection>
          {folders.map(([k, n, c]) => (
            <NavItem key={k} label={n} meta={c} active={folder === k} onClick={() => setFolder(k)} />
          ))}
        </div>
        <div className="flex min-w-0 flex-col gap-5" style={{ flex: '1 1 560px' }}>
          <Card>
            <DataTable
              rows={list}
              rowKey={(r) => r.id}
              selectedKey={selRec?.id ?? null}
              onRowClick={(r) => {
                setSel(r.id);
                setExported('');
              }}
              dense
              minWidth={720}
              empty={
                <EmptyState
                  label="No recordings"
                  hint="Sessions appear here as soon as the recorder starts."
                />
              }
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  width: '2fr',
                  cell: (r) => (
                    <CellTitle title={renameCell(r)} sub={`TC ${r.tc_start} · ${fpsLabel(r.framerate)}`} />
                  ),
                },
                {
                  key: 'event',
                  header: 'Event',
                  width: '1.4fr',
                  cell: (r) => <CellText className="text-base">{r.event_name ?? '—'}</CellText>,
                },
                {
                  key: 'date',
                  header: 'Recorded',
                  width: '1fr',
                  cell: (r) => <CellMono>{fmtDateTs(r.start_ts)}</CellMono>,
                },
                {
                  key: 'dur',
                  header: 'Duration',
                  width: '.8fr',
                  cell: (r) => <CellMono tabular>{fmtClock(dur(r))}</CellMono>,
                },
                {
                  key: 'words',
                  header: 'Words',
                  width: '.8fr',
                  cell: (r) => <CellMono tabular>{(r.words ?? 0).toLocaleString()}</CellMono>,
                },
                { key: 'storage', header: 'Storage', width: '.9fr', cell: syncPill },
              ]}
            />
          </Card>
          <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {exportCard}
            {tcCard}
          </div>
        </div>
      </div>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${selRec ? label(selRec) : ''}?`}
        meta={
          selRec
            ? `${fmtBytes(selRec.size)} · ${(selRec.words ?? 0).toLocaleString()} WORDS · ${selRec.upload_status === 'synced' ? 'ON DEVICE AND CLOUD' : 'ON DEVICE ONLY'}`
            : undefined
        }
        confirm={{
          label: 'Delete',
          destructive: true,
          onClick: async () => {
            if (selRec) await recs.remove(selRec.name);
            setConfirmDelete(false);
            setSel(null);
            toast.push('Recording deleted', 'ok');
          },
        }}
      >
        The JSONL session and its FLAC audio are removed from the device. This cannot be undone.
      </Dialog>
    </Section>
  );
}
